/* eslint-disable no-nested-ternary */
import React, { FunctionComponent, useRef, useState } from 'react';

import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import Image from 'next/image';
import Link from 'next/link';
import { BsFillEyeFill } from 'react-icons/bs';
import { FaShapes, FaShare } from 'react-icons/fa';
import { HiBadgeCheck, HiLocationMarker, HiSparkles } from 'react-icons/hi';
import { IoRocket } from 'react-icons/io5';
import { MdOutlineStorefront } from 'react-icons/md';

import { APP_CONFIG } from '@/app-config';
import { getSubCategoryById } from '@/lib/helpers';
import useAgeVerification from '@/modules/auth/hooks/VerifiyAgeHook';
import AgeVerificationModal from '@/modules/core/components/AgeVerificationModal';
import useBreakpoints from '@/modules/core/hooks/useBreakpoints';
import {
  IListingItem,
  ListingProductType,
  ListingState,
} from '@/modules/core/types';
import { genItemLink } from '@/modules/core/utils/utils';
import Price, { PriceType } from '@/tayara-kit/Price';
import Text from '@/tayara-kit/Text';

import CardTagList from './CardTagList';
import { AdType } from './types';
import SocialShare from '../SocialShare';

dayjs.extend(relativeTime);

export enum AdCardLayout {
  Flexible,
  Fixed,
  Wide,
}

interface AdCardProps extends React.HTMLAttributes<HTMLDivElement> {
  isStatic?: boolean;
  isMyListing?: boolean;
  adCardData: IListingItem;
  adType: AdType;
  layout?: AdCardLayout;
  index?: number;
}

const AdCard: FunctionComponent<AdCardProps> = ({
  adCardData,
  adType,
  className,
  isStatic,
  isMyListing,
  // index,
  ...props
}) => {
  // const [adCardRef] = useTrackAdCardViews(adCardData.id, index);
  const standardCardWidth = useBreakpoints(APP_CONFIG.standardWidth);
  const standardCardHeight = useBreakpoints(APP_CONFIG.standardHeight);
  const containerRef = useRef<HTMLDivElement>(null);

  const imgSrc = adCardData?.images?.[0] || '';

  const metadataClassName = `text-2xs font-light 
  ${adType === AdType.Gold ? 'text-black/40' : 'text-gray-800'}`;

  const { metadata } = adCardData;
  const subCategory = getSubCategoryById(metadata?.subCategory)?.name;
  const iqosPhone = adCardData?.phone;
  const shopAvatar = adCardData?.metadata?.publisher?.avatar || '';
  const [isHovered, setIsHovered] = useState(false);
  const isNeuf = subCategory === 'Immobilier Neuf';
  const NeedToVerifyAge = adCardData.metadata?.publisher?.name === 'IQOS';
  const { isVerified, verifyAge, showModal, handleConfirm, handleCloseModal } =
    useAgeVerification();

  // Handle mouse enter event
  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  // Handle mouse leave event
  const handleMouseLeave = () => {
    setIsHovered(false);
  };
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    if (NeedToVerifyAge && !isVerified) {
      e.preventDefault();
      verifyAge();
    }
  };

  return (
    <article
      className="mx-0"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <AgeVerificationModal
        isOpen={showModal}
        onClose={handleCloseModal}
        onConfirm={() => handleConfirm(genItemLink(adCardData))}
      />
      <Link
        key={adCardData.id}
        href={{ pathname: genItemLink(adCardData) }}
        passHref
        target={'_blank'}
        onClick={handleClick}
      >
        <div
          className={`relative rounded-md group h-full border-[1px] border-gray-300 ${
            isStatic ? '' : `lg:card-hover  flex `
          } `}
          style={
            !isStatic && standardCardHeight && standardCardWidth
              ? {
                  width: `${standardCardWidth}px`,
                  height: `${standardCardHeight}px`,
                }
              : {}
          }
        >
          {isMyListing ||
          (isNeuf && adCardData?.metadata?.state === ListingState.SOLD) ? (
            <CardTagList
              isShop={false}
              listingState={adCardData.metadata?.state}
              meiliSynced={adCardData.metadata?.meiliSynced}
            />
          ) : (
            <></>
          )}
          {isHovered &&
          isMyListing &&
          adCardData.metadata?.state === ListingState.ACTIVE ? (
            <div className="w-full absolute justify-center flex items-center flex-col gap-y-2 left-[0%] top-[18%] z-[41]">
              <Link
                key={adCardData.id}
                href={{ pathname: genItemLink(adCardData) }}
                passHref
                target={'_blank'}
              >
                <button className="w-full flex items-center rounded-full border-[1px] text-white hover:text-gray-700 hover:border-white p-2.5 gap-x-2 bg-white/20 hover:bg-white/95 hover:shadow-inner hover:shadow-neutral-400">
                  <BsFillEyeFill size={16} className="" />
                  {/* <span className="tracking-wide truncate text-xs font-bold  ">Consulter l'annonce</span> */}
                </button>
              </Link>
              <SocialShare
                title="Partager l'annonce"
                shareUrl={`${APP_CONFIG.basePath}${genItemLink(adCardData)}`}
                socialIconSize={40}
                buttonElement={
                  <button
                    className="flex items-center rounded-full border-[1px] text-white hover:text-gray-700 hover:border-white w-fit p-2.5 gap-x-2 bg-white/20 hover:bg-white/95 hover:shadow-inner hover:shadow-neutral-400"
                    onClick={(e) => e.preventDefault()}
                  >
                    <FaShare size={14} className="" />
                    {/* <span className="tracking-wide truncate text-xs font-bold  ">Partager l'annonce</span> */}
                  </button>
                }
              />
              <Link href={`/boost/${adCardData.id}`} passHref>
                <button className="w-fit rounded-full border-[1px] font-bold hover:font-normal  border-white p-2.5 flex items-center gap-x-2  text-white bg-gradient-to-tr from-white/20 via-white/20 to-white/20 hover:from-yellow-500 hover:to-orange-500 bg-white/20  hover:shadow-inner hover:shadow-neutral-400 group 	">
                  <IoRocket size={14} className="" />
                  {/* <span className="tracking-widest truncate uppercase text-xs  leading-4 ">{`" Bi3 Fissa3 "`}</span> */}
                </button>
              </Link>
            </div>
          ) : (
            <></>
          )}
          <div
            className={`flex flex-col h-full w-full rounded-md overflow-hidden  pb-2  transition-all ${
              adType === AdType.Gold
                ? ' bg-amber-100 border-amber-200'
                : 'border-neutral-100 bg-neutral-100'
            } aspect-grid ${className}`}
            ref={containerRef}
            {...props}
          >
            {adCardData?.metadata?.publisher?.isShop && (
              <div className="relative z-[1] mb-[-40px]">
                <div
                  className={`transform-gpu absolute skew-y-[4deg] scale-x-[10rem]  top-0 bg-gradient-to-r ${
                    isNeuf
                      ? 'from-neuf-200 via-neuf-200/30 '
                      : 'from-primary via-primary/30 '
                  } to-transparent rounded-bl-full right-0 h-[2.8rem] w-[9rem] -z-[1]`}
                />
                <div
                  className={`transform-gpu absolute skew-y-[4deg] scale-x-[10rem]  top-0 rounded-bl-full right-0 h-[2.6rem] w-[8.85rem] -z-[1]  ${
                    adType === AdType.Gold ? 'bg-amber-100' : 'bg-neutral-100'
                  }`}
                />
                {/* Left side tags */}
                <div className="absolute top-0 left-0 w-1/3 pl-1 ">
                  {/* Top left side tags go here */}
                </div>
                <div className="flex items-center justify-end space-x-2 px-2 my-1">
                  <div className="flex flex-col items-end">
                    <span className="text-sm leading-tight capitalize text-gray-800 text-right flex items-center gap-x-0.5">
                      {/* Shop name goes here */}
                      <span className="w-[70px] overflow-hidden text-ellipsis whitespace-nowrap">
                        {adCardData?.metadata?.publisher?.name?.toLowerCase()}
                      </span>
                      {adCardData?.metadata.publisher.isApproved ? (
                        <span>
                          <HiBadgeCheck size={14} className="text-blue-500" />
                        </span>
                      ) : (
                        <></>
                      )}
                    </span>
                    <Text
                      text={isNeuf ? 'Promoteur' : 'Boutique'}
                      className={`text-2xs leading-tight ${
                        isNeuf ? 'text-neuf-200' : 'text-primary'
                      } font-bold`}
                    />
                  </div>

                  {/* Publisher Avatar */}
                  <div
                    className={`flex items-center justify-center transition-transform active:scale-90 ${className}`}
                  >
                    <div className="avatar relative">
                      <div className="mask mask-squircle absolute left-[-1.5px] top-[-1.5px] w-[31px] bg-white"></div>
                      <div className={`mask mask-squircle w-[30px]`}>
                        {adCardData?.metadata?.publisher?.avatar ? (
                          <img
                            loading="lazy"
                            className={`w-full ${
                              iqosPhone === '+21680104767'
                                ? 'ml-[3px] !w-[80%] !h-[80%]'
                                : ''
                            }`}
                            src={
                              shopAvatar.includes('http')
                                ? `https://www.tayara.tn/mediaGateway/resize-image?img=${shopAvatar.substring(
                                    shopAvatar.lastIndexOf('/') - 2
                                  )}&w=300`
                                : `https://www.tayara.tn/mediaGateway/resize-image?img=${shopAvatar}&w=300`
                            }
                            alt={`${adCardData?.metadata?.publisher?.name?.toLowerCase()} tayara publisher shop avatar`}
                          />
                        ) : (
                          <div className="bg-blue-400 text-blue-100 w-full h-full flex">
                            <MdOutlineStorefront className="m-auto" />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div className="relative w-full  h-full grow -mb-10">
              <div className={`w-full  aspect-video`}></div>
              {/* <div className="absolute z-[9] inset-0 flex place-items-center align-items-center pointer-events-none">
                <TkT
                  size={30}
                  className={`text-white m-auto ${
                    firstImageLoaded ? 'opacity-10' : 'animate-pulse opacity-20'
                  }`}
                />
              </div> */}
              <Image
                src={
                  imgSrc.includes('http')
                    ? `https://www.tayara.tn/mediaGateway/resize-image?img=${imgSrc.substring(
                        imgSrc.lastIndexOf('/') - 2
                      )}&w=300`
                    : `https://www.tayara.tn/mediaGateway/resize-image?img=${imgSrc}&w=300`
                }
                unoptimized
                loading="lazy"
                {...(adCardData.imgLoad
                  ? {
                      placeholder: 'blur',
                      blurDataURL: `data:image/webp;base64,${adCardData.imgLoad}`,
                    }
                  : {})}
                className={`bg-neutral-300 w-full h-full object-cover ${
                  isHovered && isMyListing ? 'brightness-50' : ''
                } `}
                fill
                alt={`${adCardData.title}`}
              />
              {/* <CardCarousel
            imgLoad={adCardData.imgLoad}
            images={adCardData.images}
            adType={adType}
          /> */}

              {/* Angled block (incliné) */}
              <div
                className={`absolute skew-y-[4deg] scale-x-105 -bottom-4 h-16 left-0 right-0 w-full z-[2] bg-neutral-100 ${
                  adType === AdType.Gold
                    ? 'bg-amber-100'
                    : 'bg-neutral-100 border-t border-t-neutral-100'
                }`}
              >
                {adCardData?.metadata?.publisher?.isShop && (
                  <div
                    className={`relative top-[-2px] h-[4px] w-full z-[5] bg-gradient-to-r  from-transparent ${
                      isNeuf
                        ? 'via-neuf-100/50 to-neuf-100'
                        : 'via-primary/50 to-primary'
                    }`}
                  />
                )}
              </div>
            </div>
            <div
              className={`pb-2 px-3 z-10 flex-none ${
                adCardData?.price > 1 && '-mt-1'
              }`}
            >
              {adCardData?.metadata?.producttype === ListingProductType.New && (
                <div className="absolute bottom-[0.7rem] left-[70%] flex items-center ">
                  <span className="flex items-center gap-x-1 rounded-md text-blue-100 text-[7px] md:text-3xs font-extrabold px-1 font-arabic tracking-widest bg-primary py-1 lg:ml-2">
                    <HiSparkles size={10} />
                    NEUF
                  </span>
                </div>
              )}
              {adCardData?.price > 1 ? (
                <Price
                  price={adCardData?.price}
                  type={
                    isNeuf
                      ? PriceType.Neuf
                      : adType === AdType.Gold
                      ? PriceType.Gold
                      : PriceType.Normal
                  }
                />
              ) : null}
              <h2
                className={`card-title font-arabic text-sm font-medium leading-5 text-gray-800 max-w-min min-w-full line-clamp-2 my-2`}
                dir="auto"
              >
                {adCardData?.title}
              </h2>

              {/* Horizontal divider */}
              <div className="mt-1 mb-3 h-[1px] bg-black/5"></div>

              {/* Metadata */}

              <div
                className={`flex items-center space-x-1 mb-[1px] ${metadataClassName}`}
              >
                {subCategory ? (
                  <FaShapes size={11} className="text-neutral-300 mr-1" />
                ) : null}
                <Text
                  className=" truncate text-3xs md:text-xs lg:text-xs w-3/5 font-medium text-neutral-500"
                  text={subCategory}
                />
              </div>
              <div
                className={`flex items-center space-x-1 ${metadataClassName}`}
              >
                <HiLocationMarker
                  size={11}
                  className="text-neutral-300 mr-1 "
                />
                <Text
                  className="line-clamp-1 truncate text-3xs md:text-xs lg:text-xs w-3/5 font-medium text-neutral-500"
                  text={`${adCardData.location?.governorate}, ${dayjs(
                    adCardData.metadata?.publishedOn
                  ).fromNow()}`}
                />
              </div>
            </div>
          </div>
        </div>
      </Link>
    </article>
  );
};

export default AdCard;
